
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
import External from 'plaid-threads/Icons/External';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  section: 'Signal',
  title: 'Introduction',
  description: 'Evaluate ACH payment risk'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Overview.Header product={'Signal'}>{metadata.description}</Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Signal'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/signal/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='/docs/quickstart' />
  <Actions.SmallCard icon={<External mdxType="External" />} title='Demo' link='https://plaid.coastdemo.com/share/6786ccc5a048a5f1cf748cb5?zoom=100' /> 
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "signal-overview"
    }}>{`Signal overview`}</h4>
    <p>{`Plaid Signal is Plaid's solution for ACH risk management. With Signal, you can use Signal Rules in the Plaid Dashboard to create and manage business logic for handling transactions.`}</p>
    <p>{`Plaid Signal includes two separate products: `}<a parentName="p" {...{
        "href": "/docs/balance"
      }}>{`Balance`}</a>{`, which gets real-time balances; and `}<a parentName="p" {...{
        "href": "#signal-transaction-scores-overview"
      }}>{`Signal Transaction Scores`}</a>{`, which uses ML modeling to assess transaction risk using over 80 attributes. You can purchase and use either Balance or Signal Transaction Scores by itself, or combine them for a more comprehensive ACH risk management approach. `}</p>
    <p>{`Signal products simplify managing transaction risk with the no-code `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signal/risk-profiles"
      }}>{`Signal Rules Dashboard`}</a>{`, which allows you to easily configure risk rules and react quickly to changing trends.`}</p>
    <h4 {...{
      "id": "signal-transaction-scores-overview"
    }}>{`Signal Transaction Scores overview`}</h4>
    <YouTubeEmbed videoId='HXxbhK6jfKY' alignment='full' title='Signal Transaction Scores in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Signal Transaction Scores works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <p>{`Signal Transaction Scores applies machine learning to linked bank account data to predict the likelihood that a transaction will result in an ACH return. Signal Transaction Scores simplifies payment risk management by:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Evaluating transactions at ultra-low latency (p95 < 2 seconds) so you can incorporate risk evaluations into critical user-present interactions, like account funding or purchase flows  `}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Powering management via Signal Rules, including a rule optimization platform that incorporates industry benchmarks, backtesting, and personalized rule suggestions based on your business's transaction activity, making it easy to tune your thresholds for maximized revenue`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Returning over 80 predictive insights that you can incorporate into your own risk assessment models`}</p>
      </li>
    </ul>
    <p>{`The Signal Platform considers over 1,000 risk factors to evaluate proposed transactions. Over time, as you use Signal Transaction Scores, it will provide more customized and refined recommendations.`}</p>
    <p>{`Signal Transaction Scores can evaluate the risk of US domestic transactions over ACH (both Standard and Same Day ACH). Signal products cannot be used to evaluate RTP or RfP transactions, debit card transactions, transactions to or from a non-US bank account, or wire transfers. For these use cases, use `}<a parentName="p" {...{
        "href": "/docs/balance/"
      }}>{`Balance`}</a>{` with `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` instead. For more details, see `}<a parentName="p" {...{
        "href": "/docs/payments/#balance-and-signal-transaction-scores-comparison"
      }}>{`Signal Transaction Scores vs. Balance comparison chart`}</a>{`.`}</p>
    <h4 {...{
      "id": "how-signal-transaction-scores-works"
    }}>{`How Signal Transaction Scores works`}</h4>
    <p>{`First, Plaid analyzes and summarizes the level of risk a transaction poses into a `}<a parentName="p" {...{
        "href": "#signal-transaction-scores"
      }}>{`risk score`}</a>{` called a Signal Transaction Score.`}</p>
    <p>{`Next, a `}<a parentName="p" {...{
        "href": "#signal-rulesets"
      }}>{`ruleset`}</a>{` is applied to turn these scores into actions. You must create and tune these rulesets to match your business's risk tolerance.`}</p>
    <h5 {...{
      "id": "signal-transaction-scores"
    }}>{`Signal Transaction Scores`}</h5>
    <p>{`When you call `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, Plaid generates a score for a proposed transaction, predicting the likelihood of returns due to insufficient funds, closed or frozen accounts, and other administrative bank returns, as well as consumer authorized returns. A higher score indicates a greater likelihood that the transaction will result in an ACH return.`}</p>
    <h5 {...{
      "id": "signal-rulesets"
    }}>{`Signal rulesets`}</h5>
    <p>{`To turn these risk scores into an action, you will configure a `}<a parentName="p" {...{
        "href": "/docs/signal/signal-rules"
      }}>{`Signal Ruleset`}</a>{`. Plaid can suggest an initial set of rules to approve payments below a certain score threshold. Signal Transaction Scores provides you with access to both the aggregate score and over 80 predictive insights, allowing you to set up simple score-based rulesets or to create more complex rules.`}</p>
    <Image src='/assets/img/docs/signal/performance-page.png' alt='Image of example distribution of Plaid Signal Transaction Scores' expandable={true} mdxType="Image" />
    <p>{`The shape of this graph (how many transactions are low risk versus high risk) is unique per customer. `}<a parentName="p" {...{
        "href": "/docs/signal/reporting-returns"
      }}>{`Reporting returns`}</a>{` will allow the Signal Dashboard to provide you with personalized recommendations for `}<a parentName="p" {...{
        "href": "/docs/signal/signal-rules"
      }}>{`adjusting score thresholds`}</a>{`.`}</p>
    <p>{`It is recommended that you roll out Signal Transaction Scores in stages to collect data, then use the Dashboard tools to adjust your approval logic at each rollout phase.`}</p>
    <h5 {...{
      "id": "monitoring-integration-health-and-returns"
    }}>{`Monitoring integration health and returns`}</h5>
    <p>{`The Signal Overview page in the Dashboard tracks your integration health, as well as return rates, your top return codes, and the institutions contributing the most returns.`}</p>
    <Image src='/assets/img/docs/signal/signal-overview.png' alt='Signal Overview page showing integration health and returns analytics' expandable={true} mdxType="Image" />
    <p>{`Note that the return rate in the returns graph will differ slightly from the return rate reported in the integration health overview, as they reflect different metrics: transactions where no decision was reported are omitted from the denominator in the integration health graph, while the returns graph reflects all transactions in the denominator. The return graph is also based on a weekly data update, while the transactions integration health graph is updated in real time.`}</p>
    <h4 {...{
      "id": "integration-overview"
    }}>{`Integration overview`}</h4>
    <Callout warning mdxType="Callout">
      <p>{`If you already use Transfer, do not follow this standalone integration path. Instead, see `}<a parentName="p" {...{
          "href": "/docs/transfer/signal-rules"
        }}>{`Using Signal with Transfer`}</a>{` for instructions.`}</p>
    </Callout>
    <ol>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "/docs/signal/creating-signal-items"
          }}>{`Create a new Item with Signal`}</a>{` or `}<a parentName="p" {...{
            "href": "/docs/signal/creating-signal-items#adding-signal-to-existing-items"
          }}>{`add Signal to an existing Item`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "/docs/signal/signal-rules"
          }}>{`Create a Signal ruleset`}</a>{` using the Dashboard.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` and `}<a parentName="p" {...{
            "href": "/docs/signal/signal-rules/#using-signal-ruleset-results"
          }}>{`determine the next steps based on results`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "/docs/signal/reporting-returns"
          }}>{`Report ACH returns and decisions`}</a>{` to Plaid.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`After launch, periodically review and `}<a parentName="p" {...{
            "href": "/docs/signal/tuning-rules/"
          }}>{`tune your Signal Rules`}</a>{` using the Dashboard.`}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "billing"
    }}>{`Billing`}</h4>
    <p>{`Signal Transaction Scores is billed on a per-request fee basis based on the number of calls to `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`. For more details, see `}<a parentName="p" {...{
        "href": "/docs/account/billing/#per-request-flat-fee"
      }}>{`per-request flat fee billing`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "signal-overview",
        level: 4,
        title: "Signal overview",
      },
{
        id: "signal-transaction-scores-overview",
        level: 4,
        title: "Signal Transaction Scores overview",
      },
{
        id: "how-signal-transaction-scores-works",
        level: 4,
        title: "How Signal Transaction Scores works",
      },
{
        id: "integration-overview",
        level: 4,
        title: "Integration overview",
      },
{
        id: "billing",
        level: 4,
        title: "Billing",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
